import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { LifeBuoy, RefreshCw } from "lucide-react";

import AppLayout from "@/components/AppLayout";
import CachedImage from "@/components/CachedImage";
import InstallGuideModal, { isStandalone } from "@/components/InstallGuideModal";
import { Botao, CartaoCapa, CartaoFoto, Lista, Secao, Trilho } from "@/components/sistema";
import { useAuth } from "@/contexts/AuthContext";
import { useLocale } from "@/contexts/LocaleContext";
import { useLocalizedRecipesTitles } from "@/hooks/useLocalizedRecipes";
import { useScreenTime } from "@/hooks/useScreenTime";
import { supabase } from "@/integrations/supabase/client";
import { getCollectionPath } from "@/lib/collectionSlugs";
import { bonusesDeQuemTem } from "@/lib/bonusModules";
import { getLocalizedRecipeById } from "@/lib/localizedRecipes";
import { bibliotecasDeRemediosDe, useOwnedModules } from "@/lib/moduleOwnership";
import { buildRecipeRoute } from "@/lib/recipeRoute";
import { hasRealImage } from "@/lib/resolveRecipeImage";
import { formatarTempo } from "@/lib/tempo";
import { toSentenceCase } from "@/lib/utils";
import defaultLogo from "@/assets/app-icon.png";

/**
 * A home.
 *
 * Referência de desenho: a home escura e dourada do Dribbble 15125197
 * (saudação + "ready to cook?", avatar, destaque "Menu for dinner", fila de
 * cartões) e as capas de foto do 27589455. Adaptada ao
 * público: corpo 18px, alvo 56px, rótulo em tudo.
 *
 * Quatro blocos, nesta ordem:
 *
 *   1. Saudação + avatar
 *   2. O destaque do dia — UMA receita do que a pessoa comprou, com foto,
 *      escolhida pelo dia (muda todo dia, igual para todo mundo que tem o
 *      mesmo produto). Responde "o que eu faço hoje?" antes de qualquer lista.
 *   3. Onde eu parei? → trilho com as últimas receitas abertas
 *   4. O que eu comprei? → a estante, uma capa por produto
 *   5. Onde estão meus bônus? → uma lista: os bônus dos produtos comprados
 *      (os prometidos na oferta) e os três do app, cada um com a origem
 *
 * O que ficou igual de comportamento: useScreenTime("home"), o guia de
 * instalação do PWA no primeiro acesso, e o caminho de recuperação de quem
 * comprou e não vê nada.
 */
type ReceitaResumo = {
  id: string;
  title: string;
  image?: string | null;
  image_group?: string | null;
  category?: string | null;
  time?: string | null;
  total_time?: string | null;
  prep_time?: string | null;
  difficulty?: string | null;
  module_id?: string | null;
  language?: string | null;
  recipe_number?: number | null;
};

const DIFICULDADE_KEY: Record<string, string> = {
  facil: "recipes.filter_difficulty_easy",
  easy: "recipes.filter_difficulty_easy",
  media: "recipes.filter_difficulty_medium",
  medium: "recipes.filter_difficulty_medium",
  dificil: "recipes.filter_difficulty_hard",
  hard: "recipes.filter_difficulty_hard",
};

const normalizar = (v: string | null | undefined) =>
  (v || "").toLowerCase().trim().normalize("NFD").replace(/[̀-ͯ]/g, "");

const Inicio = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refreshProfile, getAccessMode, effectiveProducts } = useAuth();
  const { language } = useLocale();
  useScreenTime("home");

  const { owned, recipeCountsByModule, isLoading } = useOwnedModules();
  const remedios = bibliotecasDeRemediosDe(effectiveProducts, !!user?.isAdmin);

  const displayName = user?.display_name || user?.email?.split("@")[0] || "";
  const inicial = (displayName || "?").charAt(0).toUpperCase();
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [verificando, setVerificando] = useState(false);

  // ── O destaque do dia ────────────────────────────────────────────────────
  // Quarenta receitas com foto, das mais pedidas, só dos módulos comprados.
  // O dia escolhe uma. Uma consulta pequena (11 colunas, 40 linhas) em vez
  // do catálogo inteiro, que a tela de receitas já pagina por conta própria.
  const idsModulos = useMemo(() => owned.map((o) => o.sourceId).sort(), [owned]);
  const { data: candidatasBase, isLoading: carregandoDestaque } = useQuery<ReceitaResumo[]>({
    queryKey: ["home-destaque", idsModulos.join(",")],
    queryFn: async () => {
      const { data } = await supabase
        .from("recipes")
        .select("id,title,image,image_group,category,time,total_time,prep_time,difficulty,module_id,language,recipe_number")
        .in("module_id", idsModulos)
        .order("demand_score", { ascending: false })
        .order("sort_order")
        .limit(60);
      // `recipe_number` existe na tabela mas não no tipo gerado do cliente;
      // a tela de receitas já a lê do mesmo jeito.
      return ((data || []) as unknown as ReceitaResumo[]).filter(hasRealImage).slice(0, 40);
    },
    enabled: idsModulos.length > 0,
    staleTime: 60 * 60 * 1000,
  });
  const candidatas = useLocalizedRecipesTitles(candidatasBase);
  const destaque = useMemo(() => {
    if (!candidatas || candidatas.length === 0) return null;
    const noIdioma = candidatas.filter((c) => c.language === language);
    const lista = noIdioma.length > 0 ? noIdioma : candidatas;
    const dia = Math.floor(Date.now() / 86_400_000);
    return lista[dia % lista.length];
  }, [candidatas, language]);

  const pilulasDestaque = useMemo(() => {
    if (!destaque) return [];
    const p: string[] = [];
    const tempo = formatarTempo(destaque.time || destaque.total_time || destaque.prep_time, language);
    if (tempo) p.push(tempo);
    const dif = DIFICULDADE_KEY[normalizar(destaque.difficulty)];
    if (dif) p.push(t(dif));
    return p;
  }, [destaque, language, t]);

  // ── As últimas receitas abertas, no idioma da pessoa ─────────────────────
  type ReceitaRecente = { id: string; title: string; image?: string | null; category?: string | null; time?: string | null };
  const { data: recentes = [] } = useQuery<ReceitaRecente[]>({
    queryKey: ["home-recentes", user?.id, language],
    queryFn: async () => {
      const { data } = await supabase
        .from("recipe_view_history")
        .select("recipe_id, last_viewed_at")
        .eq("user_id", user!.id)
        .order("last_viewed_at", { ascending: false })
        .limit(8);
      const localizadas = await Promise.all(
        (data || []).map((entry: { recipe_id: string }) => getLocalizedRecipeById(entry.recipe_id, language)),
      );
      const vistos = new Set<string>();
      return (localizadas as (ReceitaRecente | null)[]).filter((r): r is ReceitaRecente => {
        if (!r || vistos.has(r.id)) return false;
        vistos.add(r.id);
        return true;
      });
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });

  // ── O guia de instalação, no primeiro acesso, em qualquer aparelho ────────
  // "This isn't an app. I have to find the original email" é a maior queixa
  // da base. O InstallGuideModal detecta ios/android/desktop; aparece uma vez
  // por aparelho e some com o X. Quem já instalou (standalone) não vê nada.
  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem("install_guide_shown") === "true") return;
    const timer = setTimeout(() => {
      setShowInstallGuide(true);
      localStorage.setItem("install_guide_shown", "true");
    }, 1200);
    return () => clearTimeout(timer);
  }, []);

  const saudacao = () => {
    const hora = new Date().getHours();
    if (hora < 12) return t("home.good_morning", { defaultValue: "Good morning" });
    if (hora < 17) return t("home.good_afternoon", { defaultValue: "Good afternoon" });
    return t("home.good_evening", { defaultValue: "Good evening" });
  };

  // Os bônus de quem comprou: os do produto (prometidos na oferta) e os do app.
  const bonuses = useMemo(
    () =>
      bonusesDeQuemTem(
        (pid) => {
          const modo = getAccessMode(pid);
          return modo === "owned" || modo === "subscription";
        },
        (k, f) => t(k, f ?? k),
      ),
    [getAccessMode, t],
  );

  const verificarAcesso = async () => {
    setVerificando(true);
    try { await refreshProfile(); } finally { setVerificando(false); }
  };

  return (
    <AppLayout>
      <div style={{ paddingBottom: "calc(var(--nav-baixo) + 40px)" }}>
        {/* ── Saudação + avatar ─────────────────────────────────────────── */}
        <div
          className="flex items-start justify-between gap-4"
          style={{ padding: "28px var(--gutter) 0" }}
        >
          <div className="min-w-0">
            <h1 className="font-bold leading-tight" style={{ fontSize: "var(--t-tela)" }}>
              {displayName ? `${saudacao()}, ${displayName}` : saudacao()}
            </h1>
            <p className="pt-2 leading-snug" style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)" }}>
              {t("home.subtitle_cook_today", "What would you like to cook today?")}
            </p>
          </div>
          {/* O avatar num quadrado de cantos redondos com fio de ouro
              (referência 15125197), 56px de alvo, leva ao perfil. */}
          <button
            type="button"
            onClick={() => navigate("/profile")}
            aria-label={t("more.profile", "Profile")}
            className="flex shrink-0 items-center justify-center overflow-hidden bg-card font-bold"
            style={{
              width: 56,
              height: 56,
              borderRadius: 18,
              fontSize: "var(--t-item)",
              boxShadow: "0 0 0 2px hsl(var(--accent))",
              color: "hsl(var(--accent))",
            }}
          >
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="h-full w-full object-cover" />
            ) : (
              inicial
            )}
          </button>
        </div>

        {/* ── O destaque do dia ─────────────────────────────────────────── */}
        {/* Enquanto a consulta não volta, um fantasma do mesmo tamanho segura
            o lugar: a tela não salta quando o cartão chega. */}
        {!destaque && carregandoDestaque && idsModulos.length > 0 && (
          <div style={{ padding: "24px var(--gutter) 0" }}>
            <div className="skeleton-base" style={{ aspectRatio: "4 / 3", borderRadius: "var(--raio-cartao)" }} />
          </div>
        )}
        {destaque && (
          <div style={{ padding: "24px var(--gutter) 0" }}>
            <CartaoCapa
              sobretitulo={t("home.todays_pick", "Today's pick")}
              nome={toSentenceCase(destaque.title)}
              pilulas={pilulasDestaque}
              proporcao="4 / 3"
              capa={
                <CachedImage
                  src={destaque.image || ""}
                  alt=""
                  imageGroup={destaque.image_group ?? undefined}
                  fallbackCategory={destaque.category}
                  className="h-full w-full object-cover"
                  fetchPriority="high"
                />
              }
              onClick={() => navigate(buildRecipeRoute(destaque))}
            />
          </div>
        )}

        {/* ── Onde eu parei ─────────────────────────────────────────────── */}
        {recentes.length > 0 && (
          <>
            <Secao>{t("home.continue_where_left", "Continue where you left off")}</Secao>
            <Trilho>
              {recentes.map((r) => (
                <CartaoFoto
                  key={r.id}
                  largura={196}
                  nome={toSentenceCase(r.title)}
                  linha2={r.time || undefined}
                  capa={
                    <CachedImage
                      src={r.image || ""}
                      alt=""
                      fallbackCategory={r.category}
                      className="h-full w-full object-cover"
                      thumbnail
                    />
                  }
                  onClick={() => navigate(buildRecipeRoute(r))}
                />
              ))}
            </Trilho>
          </>
        )}

        {/* ── A estante ─────────────────────────────────────────────────── */}
        <Secao acao={{ rotulo: t("home.see_all", "See all"), onClick: () => navigate("/recipes") }}>
          {t("home.my_library", "My Library")}
        </Secao>

        {owned.length > 0 || remedios.length > 0 ? (
          <div style={{ padding: "0 var(--gutter)", display: "grid", gap: "var(--entre-cartoes)" }}>
            {owned.map((item) => {
              const contagem = recipeCountsByModule?.[item.sourceId] ?? 0;
              return (
                <CartaoCapa
                  key={item.id}
                  nome={item.name}
                  linha2={
                    contagem > 0
                      ? `${contagem} ${t("search.recipes_count")}`
                      : t("home.app_collection_label")
                  }
                  foto={item.image || defaultLogo}
                  onClick={() => navigate(getCollectionPath(item.sourceId, item.name))}
                />
              );
            })}
            {remedios.map((b) => (
              <CartaoCapa
                key={b.productId}
                nome={b.nome}
                linha2={t("home.app_collection_label")}
                foto={b.capa}
                onClick={() => navigate(b.rota)}
              />
            ))}
          </div>
        ) : isLoading ? null : (
          /* Quem comprou e não vê nada é o ticket de suporte mais caro do app:
             quase sempre é login com outro e-mail, ou os 5 minutos que a
             liberação da Hotmart leva. Os dois caminhos de saída ficam aqui. */
          <div style={{ padding: "0 var(--gutter)", display: "grid", gap: "var(--entre-cartoes)" }}>
            <div className="bg-card px-5 py-6" style={{ borderRadius: "var(--raio-cartao)", boxShadow: "inset 0 0 0 1px hsl(var(--border))" }}>
              <p className="font-bold leading-tight" style={{ fontSize: "var(--t-item)" }}>
                {t("content.no_purchased", "No purchased content yet")}
              </p>
              <p className="pt-3 leading-snug" style={{ fontSize: "var(--t-corpo)", color: "var(--tinta-apoio)" }}>
                {t("home.no_owned_desc")}
              </p>
              <p className="pt-3 leading-snug" style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}>
                {t("home.no_owned_email_hint")}
              </p>
            </div>
            <Botao
              rotulo={verificando ? t("home.retry_checking") : t("home.retry_access")}
              icone={RefreshCw}
              desabilitado={verificando}
              onClick={verificarAcesso}
            />
            <Botao
              rotulo={t("home.open_support")}
              icone={LifeBuoy}
              variante="secundaria"
              onClick={() => navigate("/support")}
            />
          </div>
        )}

        {/* ── Bônus ─────────────────────────────────────────────────────── */}
        <Secao>{t("home.section_bonuses", "Your bonuses")}</Secao>
        <Lista
          itens={bonuses.map((b) => ({
            nome: b.nome,
            linha2: b.produto,
            icone: b.icone,
            onClick: () => navigate(b.route),
          }))}
        />
      </div>

      <InstallGuideModal open={showInstallGuide} onOpenChange={setShowInstallGuide} />
    </AppLayout>
  );
};

export default Inicio;
